import React from 'react';

import debugHook from '../hooks/hookUtils';

export default debugHook(useStoreState, 'useStoreState');

/**
 * A hook to subscribe to a store and retrieve data from it
 *
 * Accepts a string, or a function to return value/values from a store
 * If passed a string, it will return the value of that property on the state object
 * If passed a function, it will call that function passing in the store as an argument,
 *   the return value of the function will be returned to your component when the store updates
 * If not passed a stateLookup, it will return the entire store state
 *
 * examples:
 *
 * `const currentOrder = useStoreState(OrderStore, 'current');`
 *
 * ```
 *   const smartcardStatus = useStoreState(
 *     SmartcardStore,
 *     store => store.state.getIn(['smartcard', 'cardStatus'])
 *   );
 * ```
 *
 * @param {BaseStore} store - A store to subscribe to
 * @param {string|function} [stateLookup] - a state key to return, or a function which takes the store and returns the desired state
 */
export function useStoreState(store, stateLookup) {
    // use a ref so that we always have the latest version without having to memoize/hoist
    // to avoid state unnecessary updates
    const getState = React.useRef();
    getState.current = () => {
        if (typeof stateLookup === 'string') {
            return () => store.get(stateLookup);
        } else if (typeof stateLookup === 'function') {
            return () => stateLookup(store);
        } else if (stateLookup === undefined) {
            return () => store.state;
        } else {
            console.error(`Unsupported stateLookup ${stateLookup} on ${store}`);
            return () => {};
        }
    };
    const initialState = React.useMemo(() => getState.current(), []);
    const [internalState, setInternalState] = React.useState(initialState);

    // keep internal state in sync with store
    // memoize the listener, so we can unsubscribe
    const listener = React.useCallback(() => {
        return setInternalState(getState.current());
    }, []);

    // Manage store subscription
    React.useEffect(() => {
        store.addChangeListener(listener);
        return () => store.removeChangeListener(listener);
    }, [listener, store]);

    return internalState;
}



// WEBPACK FOOTER //
// ./src/stores/useStoreState.js